import { useEffect, useState } from "react";
import ModalV2 from "../../ModalV2";
import Toggle from "../../Toggle";
import Tooltip from "../../Tooltip";
import useModal from "../../../hooks/useModal";
import useNetworks from "../../../hooks/useNetworks";
import toast from "react-hot-toast";
import { Button } from "@oc-labs/wt-storybook";
import { X } from "lucide-react";
import PropTypes from "prop-types";

export default function UpsertNetworkModal({ onUpsert }) {
  const { adminUpdateNetwork, adminCreateNetwork } = useNetworks();
  const { close, data } = useModal("create-network-modal");
  const [networkData, setNetworkData] = useState({
    name: "",
    slug: "",
    symbol: "",
    chainId: "",
    networkType: "",
    rpcUrl: "",
    explorerUrl: "",
    delegationContract: "",
    displayDefault: true,
    enabled: true,
  });

  useEffect(() => {
    if (data) {
      setNetworkData({
        name: data.name || "",
        slug: data.slug || "",
        symbol: data.symbol || "",
        chainId: data.chainId || "",
        networkType: data.networkType || "",
        rpcUrl: data.rpcUrl || "",
        explorerUrl: data.explorerUrl || "",
        delegationContract: data.delegationContract || "",
        displayDefault:
          data.displayDefault !== undefined ? data.displayDefault : true,
        enabled: data.enabled !== undefined ? data.enabled : true,
      });
    } else {
      setNetworkData({
        name: "",
        slug: "",
        symbol: "",
        chainId: "",
        networkType: "",
        rpcUrl: "",
        explorerUrl: "",
        delegationContract: "",
        displayDefault: true,
        enabled: true,
      });
    }
  }, [data]);

  const generateSlug = (name) =>
    name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const handleCreateNetwork = async () => {
    try {
      if (data?.id) await adminUpdateNetwork(data?.id, networkData);
      else await adminCreateNetwork(networkData);
      if (onUpsert) onUpsert();
      toast.success("Network created successfully");
      close();
    } catch (error) {
      toast.error(error.message || "Failed to create network");
    }
  };

  return (
    <ModalV2 name={"create-network-modal"}>
      <div className="p-8 border-b border-gray-300 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-2">
            <h1 className="text-white text-3xl font-black leading-tight tracking-[-0.033em]">
              Add New Network
            </h1>
            <p className="text-blue-100 text-base font-normal">
              Configure advanced blockchain RPC settings and network metadata.
            </p>
          </div>
          <button className="text-gray-400 hover:text-white transition-colors" onClick={() => close()}>
            <X />
          </button>
        </div>
      </div>
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          {/* Column 1 */}
          <div className="space-y-6">
            <label className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <p className="text-gray-900 text-sm font-medium">
                  Network Name
                </p>
                <Tooltip text="The display name of the network">
                  <span className="material-symbols-outlined text-xs text-gray-500 cursor-help">
                    info
                  </span>
                </Tooltip>
              </div>
              <input
                className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                placeholder="e.g. Ethereum Mainnet"
                value={networkData.name}
                onChange={(e) =>
                  setNetworkData({
                    ...networkData,
                    name: e.target.value,
                    slug: generateSlug(e.target.value),
                  })
                }
              />
            </label>
            <label className="flex flex-col gap-2">
              <p className="text-gray-900 text-sm font-medium">Slug</p>
              <input
                className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                placeholder="eth-mainnet"
                value={networkData.slug}
                onChange={(e) =>
                  setNetworkData({ ...networkData, slug: e.target.value })
                }
              />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col gap-2">
                <p className="text-gray-900 text-sm font-medium">
                  Currency Symbol
                </p>
                <input
                  className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                  placeholder="ETH"
                  value={networkData.symbol}
                  onChange={(e) =>
                    setNetworkData({ ...networkData, symbol: e.target.value })
                  }
                />
              </label>
              <label className="flex flex-col gap-2">
                <p className="text-gray-900 text-sm font-medium">Chain ID</p>
                <input
                  className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                  placeholder="1"
                  value={networkData.chainId}
                  onChange={(e) =>
                    setNetworkData({ ...networkData, chainId: e.target.value })
                  }
                />
              </label>
            </div>
          </div>
          {/* Column 2 */}
          <div className="space-y-6">
            <label className="flex flex-col gap-2">
              <p className="text-gray-900 text-sm font-medium">Network Type</p>
              <select
                className="w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 px-4 text-base font-normal"
                value={networkData.networkType}
                onChange={(e) =>
                  setNetworkData({
                    ...networkData,
                    networkType: e.target.value,
                  })
                }
              >
                <option value="">Select network type</option>
                <option value="mainnet">Mainnet</option>
                <option value="testnet">Testnet</option>
              </select>
            </label>
            <label className="flex flex-col gap-2">
              <p className="text-gray-900 text-sm font-medium">RPC URL</p>
              <input
                className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                placeholder="https://mainnet.infura.io/v3/..."
                value={networkData.rpcUrl}
                onChange={(e) =>
                  setNetworkData({ ...networkData, rpcUrl: e.target.value })
                }
              />
            </label>
            <label className="flex flex-col gap-2">
              <p className="text-gray-900 text-sm font-medium">
                Explorer URL (Optional)
              </p>
              <input
                className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                placeholder="https://etherscan.io"
                value={networkData.explorerUrl}
                onChange={(e) =>
                  setNetworkData({
                    ...networkData,
                    explorerUrl: e.target.value,
                  })
                }
              />
            </label>
            <label className="flex flex-col gap-2">
              <p className="text-gray-900 text-sm font-medium">
                Delegation Contract
              </p>
              <input
                className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
                placeholder="0x..."
                value={networkData.delegationContract}
                onChange={(e) =>
                  setNetworkData({
                    ...networkData,
                    delegationContract: e.target.value,
                  })
                }
              />
            </label>
          </div>
        </div>
        {/* Toggles Section */}
        <div className="mt-10 pt-8 border-t border-gray-300 grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-300">
            <div className="flex flex-col">
              <span className="text-gray-900 font-medium">
                Display by Default
              </span>
              <span className="text-gray-600 text-xs">
                Show in the primary wallet list
              </span>
            </div>
            <Toggle
              checked={networkData.displayDefault}
              onChange={(value) =>
                setNetworkData({ ...networkData, displayDefault: value })
              }
              text="Display by Default"
            />
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-300">
            <div className="flex flex-col">
              <span className="text-gray-900 font-medium">Enabled</span>
              <span className="text-gray-600 text-xs">
                Allow transactions on this network
              </span>
            </div>
            <Toggle
              checked={networkData.enabled}
              onChange={(value) =>
                setNetworkData({ ...networkData, enabled: value })
              }
              text="Enabled"
            />
          </div>
        </div>
        {/* Footer Actions */}
        <div className="flex justify-end gap-4 mt-6">
          <div>
            <Button type="tertiary" onClick={() => close()}>
              Cancel
            </Button>
          </div>
          <div>
            <Button type="primary" onClick={handleCreateNetwork}>
              {data?.id ? "Update Network" : "Create Network"}
            </Button>
          </div>
        </div>
      </div>
    </ModalV2>
  );
}

UpsertNetworkModal.propTypes = {
  onUpsert: PropTypes.func,
};